import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const AdvancedSEO = ({ 
  title,
  description,
  keywords,
  image = "/images/maxicar-og.jpg",
  type = "website",
  author = "MaxiCar Team",
  customMeta = []
}) => {
  const location = useLocation();
  
  // Detect current domain for dynamic SEO - more robust detection
  const currentDomain = window.location.origin;
  const isMaxicarWorms = currentDomain.includes('maxicar-worms.de') || currentDomain.includes('www.maxicar-worms.de');
  const isWormserMaxicar = currentDomain.includes('wormser-maxicar.de') || currentDomain.includes('www.wormser-maxicar.de');
  
  // Set primary domain based on current request
  let primaryDomain;
  if (isMaxicarWorms) {
    primaryDomain = 'https://www.maxicar-worms.de';
  } else if (isWormserMaxicar) {
    primaryDomain = 'https://www.wormser-maxicar.de';
  } else {
    // Default fallback
    primaryDomain = 'https://www.wormser-maxicar.de';
  }
  
  // Comprehensive keyword strategy for Worms taxi dominance
  const wormsKeywords = [
    // Primary keywords
    'taxi worms', 'taxi buchen worms', 'maxicar worms', 'bester taxi service worms',
    // Location-specific
    'taxi bahnhofstraße worms', 'taxi 67547 worms', 'taxi worms hauptbahnhof',
    'taxi worms innenstadt', 'taxi worms rheinhessen', 'taxi worms nähe',
    // Service-specific
    'flughafentransfer worms', 'airport transfer worms', 'taxi zum flughafen worms',
    'krankentransport worms', 'medizinischer transport worms', 'business taxi worms',
    // Urgent/immediate needs
    'taxi sofort worms', 'taxi jetzt worms', 'taxi 24 stunden worms', 'notfall taxi worms',
    'taxi nacht worms', 'später taxi worms', 'früher taxi worms',
    // Quality indicators
    'günstigster taxi worms', 'beste taxi worms', 'zuverlässiger taxi worms',
    'professioneller taxi worms', 'sicherer taxi worms',
    // Online booking
    'online taxi buchen worms', 'taxi app worms', 'taxi vorbestellen worms',
    'taxi reservieren worms', 'digitales taxi worms',
    // Surrounding areas
    'taxi mainz worms', 'taxi mannheim worms', 'taxi ludwigshafen worms',
    'taxi rheinhessen', 'taxi rheinland-pfalz',
    // Long-tail keywords
    'taxi worms für 4 personen', 'taxi worms für flughafen frankfurt',
    'taxi worms mit kindersitz', 'rollstuhl taxi worms', 'tier taxi worms',
    // Brand variations
    'maxicar taxi worms', 'maxicar worms buchen', 'maxicar worms telefon',
    'maxicar worms preise', 'maxicar worms bewertung'
  ];

  // Page-specific SEO optimization
  const getPageSEO = () => {
    const path = location.pathname;
    
    if (path === '/' || path === '/de' || path === '/en') {
      return {
        title: title || (isMaxicarWorms 
          ? 'MaxiCar Worms - #1 Bester Taxi Service | Online Buchen | Bahnhofstraße 30'
          : 'MaxiCar Worms - Taxi Buchen | Flughafentransfer | 24/7 Service'),
        description: description || (isMaxicarWorms
          ? 'MaxiCar Worms - Bester Online Taxi Service in Bahnhofstraße 30, 67547 Worms ✓ Sofort online Buchen ✓ Professionelle Fahrer ✓ 24/7 Service ✓ Beste Preise in Worms'
          : 'Taxi Buchen in Worms ✓ Flughafentransfer ✓ 24/7 verfügbar ✓ Professionelle Fahrer ✓ Günstige Preise ✓ Sofort online Buchen bei MaxiCar Worms'),
        keywords: keywords || wormsKeywords.join(', ')
      };
    }
    
    if (path.includes('booking') || path.includes('taxi-buchen')) {
      return {
        title: 'Taxi Buchen Worms - Sofort Online | 30 Sekunden | MaxiCar',
        description: '🚖 Taxi in Worms in 30 Sekunden online buchen ✓ Sofortbestätigung ✓ Feste Preise ✓ 24/7 verfügbar ✓ Sichere Bezahlung ✓ Professionelle Fahrer',
        keywords: 'taxi buchen worms, taxi online buchen worms, sofort taxi worms, taxi reservieren worms, maxicar buchen, taxi app worms'
      };
    }
    
    if (path.includes('airport') || path.includes('flughafentransfer')) {
      return {
        title: 'Flughafentransfer Worms - Frankfurt/Main (FRA) | MaxiCar',
        description: '✈️ Bester Flughafentransfer Worms - Frankfurt/Main ✓ Feste Preise ✓ Pünktlich ✓ 24/7 ✓ Gepäckservice ✓ Professionelle Fahrer ✓ Online Buchen',
        keywords: 'flughafentransfer worms, taxi zum flughafen worms, airport transfer worms, frankfurt flughafen taxi, taxi FRA worms, flughafen taxi worms'
      };
    }
    
    if (path.includes('services')) {
      return {
        title: 'Taxi Services Worms - 8 Professionelle Dienste | MaxiCar',
        description: '🚗 Alle Taxi Services in Worms ✓ Flughafentransfer ✓ Krankentransport ✓ Business Taxi ✓ Event Transport ✓ 24/7 Service ✓ Online Buchen',
        keywords: 'taxi services worms, taxi dienste worms, flughafentransfer, krankentransport worms, business taxi, event taxi worms'
      };
    }
    
    // Default SEO
    return {
      title: title || (isMaxicarWorms 
        ? 'MaxiCar Worms - Bester Taxi Service | Online buchen | Bahnhofstraße 30'
        : 'MaxiCar Worms - Taxi buchen | Flughafentransfer | 24/7 Service'),
      description: description || (isMaxicarWorms
        ? 'MaxiCar Worms - Bester Online Taxi Service in Bahnhofstraße 30, 67547 Worms ✓ Sofort online buchen ✓ Professionelle Fahrer ✓ 24/7 Service ✓ Beste Preise in Worms'
        : 'Taxi buchen in Worms ✓ Flughafentransfer ✓ 24/7 verfügbar ✓ Professionelle Fahrer ✓ Günstige Preise ✓ Sofort online buchen bei MaxiCar Worms'),
      keywords: keywords || wormsKeywords.join(', ')
    };
  };

  const pageSEO = getPageSEO();
  const currentUrl = `${window.location.origin}${location.pathname}`;
  
  // DOMINANT CANONICAL STRATEGY - Fix duplicate content issues
  const _canonicalUrl = currentUrl;
  
  // HREFLANG STRATEGY - Tell Google about multi-domain setup
  const alternateDomains = [
    { hreflang: 'de', href: `${primaryDomain}${location.pathname}` },
    { hreflang: 'en', href: `${primaryDomain}/en${location.pathname}` },
    { hreflang: 'fr', href: `${primaryDomain}/fr${location.pathname}` },
    { hreflang: 'x-default', href: `${primaryDomain}${location.pathname}` }
  ];
  
  // Add cross-domain alternate links
  if (isMaxicarWorms) {
    alternateDomains.push(
      { hreflang: 'de', href: `https://wormser-maxicar.de/de${location.pathname}` },
      { hreflang: 'en', href: `https://wormser-maxicar.de/en${location.pathname}` },
      { hreflang: 'fr', href: `https://wormser-maxicar.de/fr${location.pathname}` }
    );
  } else {
    alternateDomains.push(
      { hreflang: 'de', href: `https://www.maxicar-worms.de/de${location.pathname}` },
      { hreflang: 'en', href: `https://www.maxicar-worms.de/en${location.pathname}` },
      { hreflang: 'fr', href: `https://www.maxicar-worms.de/fr${location.pathname}` }
    );
  }
  
  // Enhanced structured data for maximum SEO impact
  const generateEnhancedStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${primaryDomain}#business`,
      "name": isMaxicarWorms ? "MaxiCar Worms - #1 Bester Taxi Service" : "MaxiCar Worms",
      "alternateName": [
        "MaxiCar Taxi Service", 
        "Bester Online Taxi Service Worms", 
        "MaxiCar Bahnhofstraße",
        "Taxi Worms 24/7",
        "Worms Taxi Service"
      ],
      "description": isMaxicarWorms 
        ? "🏆 #1 Bester Online Taxi Service in Worms - Bahnhofstraße 30. Professionelle Fahrer, 24/7 Service, sofort online buchen. Beste Preise in Worms mit 5-Sterne-Bewertungen!"
        : "Professioneller Taxi-Service in Worms mit 24/7 Verfügbarkeit, Flughafentransfer und günstigen Preisen. Sofort online buchen.",
      "url": primaryDomain,
      "logo": `${primaryDomain}/icons8-taxi-ios_filled-16.png`,
      "image": [
        `${primaryDomain}/icons8-taxi-ios_filled-16.png`,
        `${primaryDomain}/icons8-taxi-ios_filled-16.png`,
        `${primaryDomain}/icons8-taxi-ios_filled-16.png`
      ],
      "priceRange": "€€",
      "currenciesAccepted": "EUR",
      "paymentAccepted": ["Cash", "Credit Card", "PayPal", "Online Payment", "Apple Pay", "Google Pay"],
      "slogan": isMaxicarWorms ? "#1 Bester Online Taxi Service in Worms" : "Ihr zuverlässiger Taxi-Partner",
      "foundingDate": "2020",
      "hasMap": `https://maps.google.com/maps?q=Bahnhofstraße+30,+67547+Worms`,
      "openingHours": "Mo,Tu,We,Th,Fr,Sa,Su 00:00-23:59",
      "availableLanguage": ["German", "English", "French"],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+49 6241 4700",
        "contactType": "customer service",
        "availableLanguage": ["German", "English"],
        "email": isMaxicarWorms ? 'info@maxicar-worms.de' : 'info@wormser-maxicar.de',
        "hoursAvailable": "Mo,Tu,We,Th,Fr,Sa,Su 00:00-23:59",
        "serviceArea": "Worms und Umgebung"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Bahnhofstraße 30",
        "addressLocality": "Worms",
        "addressRegion": "Rheinland-Pfalz",
        "postalCode": "67547",
        "addressCountry": "DE"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 49.6333,
        "longitude": 8.3667
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "Worms",
          "addressCountry": "DE"
        },
        {
          "@type": "City", 
          "name": "Mainz",
          "addressCountry": "DE"
        },
        {
          "@type": "City",
          "name": "Mannheim", 
          "addressCountry": "DE"
        },
        {
          "@type": "City",
          "name": "Ludwigshafen",
          "addressCountry": "DE"
        },
        {
          "@type": "City",
          "name": "Frankfurt am Main",
          "addressCountry": "DE"
        }
      ],
      "serviceType": "Taxi Service",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Komplette Taxi Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Online Taxi Buchung",
              "description": "#1 Bester Online Service - Sofort taxi buchen in Worms",
              "areaServed": "Bahnhofstraße 30, 67547 Worms",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "EUR",
                "price": "25.00"
              }
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Flughafentransfer Worms",
              "description": "Bester Flughafentransfer Service von Bahnhofstraße Worms",
              "areaServed": "67547 Worms",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "EUR",
                "price": "45.00"
              }
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Krankentransport Worms",
              "description": "Professioneller medizinischer Transport in Worms",
              "areaServed": "Worms und Umgebung"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Business Taxi Worms",
              "description": "Premium Business Taxi Service für Geschäftsreisen"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "24/7 Notfall Taxi",
              "description": "24 Stunden Notfall Taxi Service in Worms",
              "availability": "24/7"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "256",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "name": isMaxicarWorms ? "MaxiCar Worms - #1 Bester Taxi Service" : "MaxiCar Worms"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5",
            "worstRating": "1"
          },
          "author": {
            "@type": "Person",
            "name": "Klaus M."
          },
          "reviewBody": "Bester Taxi Service in Worms! Online buchen funktioniert perfekt. Fahrer pünktlich und sehr freundlich.",
          "datePublished": "2024-10-15"
        },
        {
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "name": "MaxiCar Worms"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Sarah K."
          },
          "reviewBody": "Super Flughafentransfer! Pünktliche Abholung, sauberes Auto, fairer Preis. Immer wieder!",
          "datePublished": "2024-09-20"
        }
      ],
      "sameAs": [
        "https://www.facebook.com/MaxiCarWorms",
        "https://twitter.com/MaxiCarWorms",
        "https://www.instagram.com/maxicarworms",
        "https://www.linkedin.com/company/maxicar-worms"
      ]
    };

    // Add FAQ structured data for taxi services
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Wie schnell kann ich ein Taxi in Worms buchen?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Mit MaxiCar können Sie ein Taxi in Worms in unter 30 Sekunden online buchen. Sofortbestätigung und Fahrerzuweisung."
          }
        },
        {
          "@type": "Question",
          "name": "Bietet MaxiCar Flughafentransfer von Worms an?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, MaxiCar bietet professionellen Flughafentransfer von Worms zu allen deutschen Flughäfen, insbesondere Frankfurt/Main (FRA)."
          }
        },
        {
          "@type": "Question",
          "name": "Ist MaxiCar 24 Stunden in Worms verfügbar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Ja, MaxiCar bietet 24/7 Taxi Service in Worms und Umgebung. Rund um die Uhr verfügbar für Ihre Fahrt."
          }
        }
      ]
    };

    return [baseData, faqData];
  };

  const structuredData = generateEnhancedStructuredData();

  return (
    <Helmet>
      {/* Enhanced Meta Tags */}
      <title>{pageSEO.title}</title>
      <meta name="description" content={pageSEO.description} />
      <meta name="keywords" content={pageSEO.keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="language" content="German" />
      <meta name="geo.region" content="DE-RP" />
      <meta name="geo.placename" content="Worms" />
      <meta name="geo.position" content="49.6333;8.3667" />
      <meta name="ICBM" content="49.6333, 8.3667" />
      <meta name="revisit-after" content="3 days" />
      
      {/* Enhanced Open Graph Tags */}
      <meta property="og:title" content={pageSEO.title} />
      <meta property="og:description" content={pageSEO.description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="MaxiCar Worms" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Enhanced Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageSEO.title} />
      <meta name="twitter:description" content={pageSEO.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@maxicar" />
      <meta name="twitter:creator" content="@maxicar" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#FCD34D" />
      <meta name="msapplication-TileColor" content="#FCD34D" />
      <meta name="application-name" content="MaxiCar" />
      <meta name="apple-mobile-web-app-title" content="MaxiCar" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* COMPREHENSIVE HREFLANG TAGS - Multi-domain strategy */}
      {alternateDomains.map((domain, index) => (
        <link key={index} rel="alternate" hrefLang={domain.hreflang} href={domain.href} />
      ))}
      
      {/* Structured Data */}
      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
      
      {/* Custom Meta Tags */}
      {customMeta.map((meta, index) => (
        <meta key={index} {...meta} />
      ))}
    </Helmet>
  );
};

export default AdvancedSEO;
